import { v4 as uuidv4 } from 'uuid';

/** Cookie that stores the anonymous visitor identifier shared with Mixpanel. */
export const ANONYMOUS_ID_COOKIE = 'pilar_anon_id';

/**
 * Persist the anonymous id for 2 years. Without an explicit `maxAge` the
 * cookie becomes a session cookie and is dropped when the browser closes,
 * which would break attribution between visits.
 */
const ANONYMOUS_ID_MAX_AGE_SEC = 60 * 60 * 24 * 365 * 2;

const SESSION_STORAGE_KEY = 'visitor_session';
const SESSION_LOCAL_STORAGE_KEY = 'visitor_session_bridge';

/** Inactivity timeout matching GA/Mixpanel session semantics (35 min) */
export const SESSION_INACTIVITY_TTL_MS = 35 * 60 * 1000;

interface SessionBridge {
	data: string;
	lastActivity: number;
}

function parseJSON<T>(raw: string | null): T | null {
	if (!raw) return null;
	try {
		return JSON.parse(raw) as T;
	} catch {
		return null;
	}
}

function generateUUID(): string {
	return uuidv4();
}

/**
 * Read or create the anonymous id stored in the `pilar_anon_id` cookie.
 *
 * Shared between Mixpanel and the visitor-identity composable so both read
 * from the same source of truth. The cookie has an explicit `maxAge` so it
 * persists across browser restarts.
 */
export function getOrCreateAnonymousId(): string {
	const isDevelopment = process.env.NODE_ENV === 'development';

	const cookie = useCookie<string | null>(ANONYMOUS_ID_COOKIE, {
		maxAge: ANONYMOUS_ID_MAX_AGE_SEC,
		sameSite: 'lax',
		secure: !isDevelopment,
		default: () => null
	});

	if (!cookie.value) {
		cookie.value = generateUUID();
	}

	return cookie.value as string;
}

/**
 * Read session data from the localStorage bridge.
 * Returns the session id when valid (non-expired), or null otherwise.
 * Cleans up expired entries automatically.
 */
function readBridge(): string | null {
	try {
		const bridge = parseJSON<SessionBridge>(
			localStorage.getItem(SESSION_LOCAL_STORAGE_KEY)
		);
		if (!bridge?.data) return null;

		const elapsed = Date.now() - bridge.lastActivity;
		if (elapsed > SESSION_INACTIVITY_TTL_MS) {
			localStorage.removeItem(SESSION_LOCAL_STORAGE_KEY);
			return null;
		}

		return bridge.data;
	} catch {
		return null;
	}
}

/**
 * Write the session id to the localStorage bridge with the current timestamp.
 */
function writeBridge(sessionId: string): void {
	try {
		const bridge: SessionBridge = {
			data: sessionId,
			lastActivity: Date.now()
		};
		localStorage.setItem(SESSION_LOCAL_STORAGE_KEY, JSON.stringify(bridge));
	} catch {
		/* quota exceeded or private mode — non-critical */
	}
}

/**
 * Persist the session id to both sessionStorage and the localStorage bridge.
 */
function setSessionInStorage(sessionId: string): void {
	if (typeof window === 'undefined') return;

	try {
		sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
		writeBridge(sessionId);
	} catch (error) {
		console.error('Failed to save session id:', error);
	}
}

/**
 * Read or create the visitor session id.
 *
 * The localStorage bridge is the source of truth for the active session — it
 * survives across tabs and expires on inactivity. SessionStorage is only a
 * per-tab cache. When the bridge is expired or absent, a new id is generated
 * and written to both layers, even if the current tab still has a stale value
 * in sessionStorage.
 */
export function getOrCreateSessionId(): string {
	if (typeof window === 'undefined') return '';

	const bridgeData = readBridge();
	if (bridgeData) {
		const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
		if (stored !== bridgeData) {
			sessionStorage.setItem(SESSION_STORAGE_KEY, bridgeData);
		}
		return bridgeData;
	}

	const newId = generateUUID();
	setSessionInStorage(newId);
	return newId;
}

/**
 * Copy valid bridge data into sessionStorage for the current tab, generating
 * a new session id if neither layer has one. Called on plugin init so new
 * tabs inherit (or seed) the session.
 */
export function syncSessionToSessionStorage(): void {
	if (typeof window === 'undefined') return;
	getOrCreateSessionId();
}

/**
 * Heartbeat: refresh the bridge timestamp so the inactivity timer resets.
 * Should be called on every page load and client-side navigation.
 *
 * Does NOT recreate the bridge if it has already expired — the next call to
 * `getOrCreateSessionId` is responsible for generating a new session id.
 */
export function refreshSessionActivity(): void {
	if (typeof window === 'undefined') return;

	try {
		const bridge = parseJSON<SessionBridge>(
			localStorage.getItem(SESSION_LOCAL_STORAGE_KEY)
		);
		if (!bridge?.data) return;

		const elapsed = Date.now() - bridge.lastActivity;
		if (elapsed > SESSION_INACTIVITY_TTL_MS) {
			localStorage.removeItem(SESSION_LOCAL_STORAGE_KEY);
			return;
		}

		bridge.lastActivity = Date.now();
		localStorage.setItem(SESSION_LOCAL_STORAGE_KEY, JSON.stringify(bridge));
	} catch {
		/* non-critical */
	}
}
